import { OrganizationListEntityResponseDto, OrganizationListResponseDto } from '@/lib/interfaces/organisation';
import { Pagination } from '@/lib/interfaces/utils';
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable
} from '@tanstack/react-table';
import { Clock3 } from 'lucide-react';
import { Dispatch, SetStateAction, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { capitalizeFirstLetter, formatDateDetail, formatDateTime } from '../../../lib/hooks/utils';
import BankIcon from '../../../ui/components/icons/bank-icon';
import ContractIcon from '../../../ui/components/icons/contract-icon';
import FireIconMarked from '../../../ui/components/icons/fire-icon-marked';
import RocketIcon from '../../../ui/components/icons/rocket-icon';
import StarIcon from '../../../ui/components/icons/star-icon';
import { Badge } from '../../../ui/components/ui/badge';
import { Button } from '../../../ui/components/ui/button';
import Preloader from '../../../ui/components/ui/preloader';
import { StarsRating } from '../../../ui/components/ui/stars-rating';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../ui/components/ui/table';

interface DataProps {
  data: OrganizationListResponseDto[];
  setReload: Dispatch<SetStateAction<boolean>>;
  setPagination: Dispatch<SetStateAction<Pagination>>;
  pageCount: number;
  pagination: Pagination;
  isLoading: boolean;
  listEntities?: OrganizationListEntityResponseDto;
  // globalFilter: string;
  // setGlobalFilter: Dispatch<SetStateAction<string>>;
}

export const OrganisationDataTable: React.FC<DataProps> = ({ data, setReload, setPagination, pageCount, pagination, isLoading, listEntities }) => {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [rowSelection, setRowSelection] = useState({});
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const columns: ColumnDef<OrganizationListResponseDto>[] = [
    {
      id: 'picture',
      header: '',
      cell: ({ row }) => {
        return (
          <div className={`max-w-[54px] relative min-w-12 `}>
            <img
              src={row.original.profilePictureUrl ?? '/user-default.svg'}
              alt={row.original.name}
              width={54}
              height={54}
              className="rounded-full h-14 w-14 object-cover"
            />
            {row.original.isMarkedAsFavorite && (
              <div
                className={`flex absolute text-white items-center justify-center rounded-full -bottom-1 w-[27px] h-[27px] -right-1 border-[3px] border-white bg-[#12282A]`}>
                <FireIconMarked />
              </div>
            )}
          </div>
        );
      }
    },

    {
      accessorKey: 'name',
      header: ({ column }) => {
        return (
          <div className="flex gap-2 items-center">
            <p className="flex justify-center items-center gap-1 cursor-pointer" onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}>
              Unternehmen
              {/* {column.getIsSorted() === "desc" ? <ChevronDown size="18" /> : <ChevronUp size="18" />} */}
            </p>
          </div>
        );
      },
      cell: ({ row }) => {
        const organisation = row.original;
        return (
          <div className="flex flex-col text-sm">
            <p className="font-bold">
              {capitalizeFirstLetter(organisation.name)}{' '}
              {organisation.isTrialing && (
                <Badge variant="outline" className="ml-2 text-[10px] leading-none text-blue-600 border-blue-400 bg-blue-50 px-2 py-1">
                  Testphase
                </Badge>
              )}
            </p>
            <p className="text-[#888] font-medium">Kundennummer: {organisation.customerNumber}</p>
          </div>
        );
      }
    },
    {
      accessorKey: 'date',
      header: ({ column }) => {
        return (
          <p
            className="flex justify-center items-center gap-1 cursor-pointer min-w-32"
            onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}>
            Letzter Login
            {/* {column.getIsSorted() ? (column.getIsSorted() === "desc" ? " 🔽" : " 🔼") : ""} */}
            {/* {column.getIsSorted() === "desc" ? <ChevronDown size="18" /> : <ChevronUp size="18" />} */}
          </p>
        );
      },
      cell: ({ row }) => {
        return (
          <div className="flex justify-center items-center gap-2 text-sm text-[#D0D5DD]">
            <Clock3 size="18" />
            {formatDateTime(row.original.lastLogin)}
          </div>
        );
      }
    },
    {
      accessorKey: 'rating',
      header: ({ column }) => {
        return (
          <p
            className="flex justify-center items-center gap-1 cursor-pointer min-w-32"
            onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}>
            Bewertung
            {/* {column.getIsSorted() ? (column.getIsSorted() === "desc" ? " 🔽" : " 🔼") : ""} */}
            {/* {column.getIsSorted() === "desc" ? <ChevronDown size="18" /> : <ChevronUp size="18" />} */}
          </p>
        );
      },
      cell: ({ row }) => {
        return (
          <div className="flex justify-center items-center gap-2 text-sm text-[#D0D5DD]">
            <StarsRating starAmount={row.original.rating} />
          </div>
        );
      }
    },
    {
      accessorKey: 'customerNumber',
      header: ({ column }) => {
        return (
          <p className="flex justify-center items-center gap-1 cursor-pointer" onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}>
            Mitarbeiter
            {/* {column.getIsSorted() === "desc" ? <ChevronDown size="18" /> : <ChevronUp size="18" />} */}
          </p>
        );
      },
      cell: ({ row }) => <AssignedMembersCell organisation={row.original} />
    },
    {
      accessorKey: 'belongsToStatus',
      header: '',
      cell: ({ row }) => {
        const status = listEntities?.status.find((s) => s.id === row.original.belongsToStatus);

        let icon;

        switch (status?.icon) {
          case 'stars':
            icon = <StarIcon />;
            break;
          case 'rocket':
            icon = <RocketIcon />;
            break;
          case 'contract':
            icon = <ContractIcon />;
            break;
          case 'bank':
            icon = <BankIcon />;
            break;
          default:
            icon = <ContractIcon />;
            break;
        }

        const colorStyle = status?.color
          ? {
              borderColor: status.color,
              color: status.color,
              backgroundColor: `${status.color}33` // Hex with 20% opacity
            }
          : {};

        return (
          <div className="flex justify-end items-center text-sm">
            <div className="border w-fit rounded-2xl px-3 py-1 font-bold flex gap-2 items-center" style={colorStyle}>
              {icon}
              <p>
                {status ? (
                  <>{status.needsStatusDate ? `${status.value} - ${formatDateDetail(row.original.statusDate)}` : status.value}</>
                ) : (
                  'Status nicht gefunden'
                )}
              </p>
            </div>
          </div>
        );
      }
    }
  ];

  const table = useReactTable({
    data,
    columns,
    pageCount,
    manualPagination: true,
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    // onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    // onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    // globalFilterFn: globalFilterFunction,
    // onGlobalFilterChange: setGlobalFilter,
    initialState: { pagination: { pageSize: 5 } },
    state: {
      pagination,
      sorting,
      rowSelection
      // globalFilter,
    }
  });

  return (
    <div className="w-full">
      <div className="rounded-md overflow-hidden">
        {isLoading ? (
          <Preloader />
        ) : (
          <Table className="border-separate border-spacing-y-4">
            <TableHeader>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => {
                    return (
                      <TableHead key={header.id} className="text-[#D0D5DD] h-6">
                        {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                      </TableHead>
                    );
                  })}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {table.getRowModel().rows?.length ? (
                <>
                  {table.getRowModel().rows.length
                    ? table.getRowModel().rows.map((row, i) => (
                        <TableRow
                          key={row.id}
                          className={` bg-white hover:bg-white ${row.original.isOpenable ? 'cursor-pointer' : ''}`}
                          onClick={() => {
                            if (row.original.isOpenable) {
                              navigate(`/organisations/${row.original._id}`);
                            }
                          }}>
                          {row.getVisibleCells().map((cell, j) => (
                            <TableCell
                              key={cell.id}
                              className={`border-none p-2 ${j === 0 ? 'w-24' : ''} ${
                                j === 0 ? 'rounded-l-[61px]' : ''
                              } ${j === row.getVisibleCells().length - 1 ? 'rounded-r-[61px]' : ''}`}>
                              {flexRender(cell.column.columnDef.cell, cell.getContext())}
                            </TableCell>
                          ))}
                        </TableRow>
                      ))
                    : ' '}
                </>
              ) : (
                <TableRow>
                  <TableCell colSpan={columns.length} className="h-24 text-center">
                    Du hast noch keine Organisation angelegt
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        )}
      </div>

      <div className="flex items-center justify-between py-4 my-2">
        <Button
          className="text-[17px] text-[#12282A]"
          variant="outline"
          size="sm"
          onClick={() => {
            table.previousPage();
            const params = new URLSearchParams(searchParams);
            params.set('page', table.getState().pagination.pageIndex.toString());
            setSearchParams(params);
          }}
          disabled={!table.getCanPreviousPage()}>
          zurück
        </Button>
        <span className="text-sm text-[#D0D5DD]">
          Seite {table.getState().pagination.pageIndex + 1} von {table.getPageCount()}
        </span>

        <Button
          className="text-[17px] text-[#12282A]"
          variant="outline"
          size="sm"
          onClick={() => {
            table.nextPage();
            const params = new URLSearchParams(searchParams);
            params.set('page', (table.getState().pagination.pageIndex + 2).toString());
            setSearchParams(params);
          }}
          disabled={!table.getCanNextPage()}>
          nächste
        </Button>
      </div>
    </div>
  );
};

/**
 * Assignment badges of a list row.
 *
 * Single assignment shows the one contact, multiple assignment one badge per member including his share.
 * Members with share 0 are representatives and keep the separate blue styling in both modes.
 */
const AssignedMembersCell = ({ organisation }: { organisation: OrganizationListResponseDto }) => {
  const assignedMembers = organisation.assignedInternalUsers ?? [];

  const carrierNames = organisation.multipleAssignmentEnabled
    ? assignedMembers.filter((member) => member.capacity > 0).map((member) => member.fullName)
    : organisation.assignedInternalUserFullName
      ? [organisation.assignedInternalUserFullName]
      : [];

  const representativeNames = organisation.multipleAssignmentEnabled
    ? assignedMembers.filter((member) => member.capacity === 0).map((member) => member.fullName)
    : organisation.assignedRepresentativeUserFullName
      ? [organisation.assignedRepresentativeUserFullName]
      : [];

  // A single name stays readable, several would blow up the row - then the count says more at a glance.
  const carrierLabel = carrierNames.length === 0 ? 'Nicht zugewiesen' : carrierNames.length === 1 ? carrierNames[0] : `${carrierNames.length} Mitarbeiter`;

  const representativeLabel =
    representativeNames.length === 0 ? null : representativeNames.length === 1 ? representativeNames[0] : `${representativeNames.length} Vertretungen`;

  return (
    <div className="flex flex-wrap justify-center items-center text-sm gap-2">
      <div className="border border-[#D0D5DD] w-fit text-center rounded-2xl px-3 py-1 font-bold">
        <p>{carrierLabel}</p>
      </div>

      {representativeLabel && (
        <div className="border border-blue-500 bg-blue-50 w-fit text-center rounded-2xl px-3 py-1 font-bold flex items-center gap-1">
          <span className="bg-blue-500 rounded-full w-2 h-2 inline-block flex-shrink-0"></span>
          <p className="text-blue-700">{representativeLabel}</p>
        </div>
      )}
    </div>
  );
};
